import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import { SpecValuePicker } from '@/tiktok-platform/modules/products/components/SpecSupplySection';
import { makeCustomSpecKey, isCustomSpecKey, customLabelFromKey } from '@/tiktok-platform/utils/tiktokSizeChart';
import {
  formatTiktokSizeChartMeasureLabel,
  getCachedTiktokSizeChartMeasureLabel,
  resolveTiktokSizeChartMeasureLabels,
} from '@/tiktok-platform/utils/tiktokSizeChartMeasurementLabels';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

export const MEASUREMENT_TYPE_LABELS = {
  GENERIC_MEASUREMENTS: '通用测量',
  BODY_MEASUREMENTS: '人体测量',
  PRODUCT_MEASUREMENTS: '成衣/产品测量',
};

export const UNIT_SYSTEM_OPTIONS = [
  { value: 'METRIC', label: fixedUi('公制 (METRIC)') },
  { value: 'IMPERIAL', label: fixedUi('英制 (IMPERIAL)') },
];

const SIZE_HEADER = { id: 'size_chart_size_system_size', name: 'Size' };

/** 尺寸下拉顶部：均码；选中后与其它尺码互斥 */
export const ONE_SIZE_KEY = 'ONE_SIZE';
const ONE_SIZE_OPTION = { id: ONE_SIZE_KEY, name: '均码 (one size)' };

function isOneSizeKey(key) {
  return String(key || '') === ONE_SIZE_KEY;
}

function unwrapSizeChartRules(res) {
  const sizeChart = res?.sizeChart
    || res?.data?.size_chart
    || res?.response?.data?.size_chart
    || null;
  return sizeChart && typeof sizeChart === 'object' ? sizeChart : null;
}

function pickInitialMeasurementType(groups = []) {
  const preferred = ['BODY_MEASUREMENTS', 'PRODUCT_MEASUREMENTS', 'GENERIC_MEASUREMENTS'];
  for (const key of preferred) {
    if (groups.some((g) => g.measurement_type === key || g.measurementType === key)) {
      return key;
    }
  }
  const first = groups[0];
  return first?.measurement_type || first?.measurementType || 'BODY_MEASUREMENTS';
}

export function optionsForType(groups, measurementType) {
  const hit = (groups || []).find(
    (g) => (g.measurement_type || g.measurementType) === measurementType,
  );
  const options = hit?.options || [];
  return (Array.isArray(options) ? options : []).map((opt) => ({
    id: String(opt.id || '').trim(),
    name: String(opt.name || opt.id || '').trim(),
    role: opt.role,
    unit: opt.unit,
  })).filter((o) => o.id);
}

/**
 * 选尺码后表格默认带上该类目全部测量列（必填 KEY + 可选），无需用户勾选。
 */
export function defaultSelectedOptionIdsForType(groups, measurementType) {
  const nextOptions = optionsForType(groups, measurementType);
  if (!nextOptions.length) return [];
  return nextOptions.map((o) => o.id);
}

function sizeLabelFromKey(key, options = []) {
  const id = String(key || '');
  if (isOneSizeKey(id)) return fixedUi('均码');
  if (isCustomSpecKey(id)) return customLabelFromKey(id);
  const hit = options.find((o) => String(o.id) === id);
  return hit?.name || id;
}

function rowsFromSizeKeys(keys, options, prevRows = []) {
  const prevByKey = new Map();
  const prevBySize = new Map();
  (prevRows || []).forEach((row) => {
    if (row?.sizeKey) prevByKey.set(String(row.sizeKey), row);
    if (row?.size) prevBySize.set(String(row.size).trim().toLowerCase(), row);
  });
  return (keys || []).map((key) => {
    const size = sizeLabelFromKey(key, options);
    const prev = prevByKey.get(String(key))
      || prevBySize.get(String(size).trim().toLowerCase())
      || null;
    return {
      sizeKey: String(key),
      size,
      values: prev?.values && typeof prev.values === 'object' ? { ...prev.values } : {},
    };
  });
}

/**
 * 从已有 sizeChartData / sizeChartDatas 还原编辑态。
 */
export function hydrateStructuredDraft(payload = {}) {
  const data = payload?.sizeChartData
    || payload?.officialSizeChart?.sizeChartData
    || (Array.isArray(payload?.sizeChartDatas) ? payload.sizeChartDatas[0]?.size_chart_data : null)
    || (Array.isArray(payload?.sizeChartDatas) ? payload.sizeChartDatas[0]?.sizeChartData : null)
    || null;
  if (!data || typeof data !== 'object') {
    return {
      measurementType: 'BODY_MEASUREMENTS',
      unitSystem: 'METRIC',
      isOneSize: false,
      description: '',
      sizeTypes: ['size_chart_size_type_regular'],
      selectedOptionIds: [],
      selectedSizeKeys: [],
      rows: [],
    };
  }
  const headers = Array.isArray(data.table?.headers) ? data.table.headers : [];
  const measureHeaders = headers.filter((h) => String(h?.id || '') !== SIZE_HEADER.id);
  const rowsRaw = Array.isArray(data.table?.rows) ? data.table.rows : [];
  const rows = rowsRaw.map((row) => {
    const cells = Array.isArray(row?.cells) ? row.cells : [];
    const size = String(cells[0]?.value || '').trim();
    const values = {};
    measureHeaders.forEach((h, idx) => {
      values[String(h.id)] = String(cells[idx + 1]?.value || '').trim();
    });
    const isOne = /^(ONE_SIZE|均码)$/i.test(size)
      || data.is_one_size === true
      || data.isOneSize === true;
    const sizeKey = isOne ? ONE_SIZE_KEY : (size ? makeCustomSpecKey(size) : '');
    return { size: isOne ? 'ONE_SIZE' : size, sizeKey, values };
  }).filter((r) => r.size || Object.keys(r.values).length);
  const isOneSize = data.is_one_size === true
    || data.isOneSize === true
    || (rows.length === 1 && isOneSizeKey(rows[0]?.sizeKey));
  return {
    measurementType: String(data.measurement_type || data.measurementType || 'BODY_MEASUREMENTS'),
    unitSystem: String(data.unit_system || data.unitSystem || 'METRIC'),
    isOneSize,
    description: String(data.description || '').trim(),
    sizeTypes: Array.isArray(data.size_types)
      ? data.size_types.map(String)
      : (Array.isArray(data.sizeTypes) ? data.sizeTypes.map(String) : ['size_chart_size_type_regular']),
    selectedOptionIds: measureHeaders.map((h) => String(h.id)),
    selectedSizeKeys: isOneSize
      ? [ONE_SIZE_KEY]
      : rows.map((r) => r.sizeKey).filter(Boolean),
    rows: rows.length ? rows : [],
  };
}

/**
 * 保存前裁剪列：KEY 必保留；选填列仅当整列都有值时保留（整列空则不提交，对齐平台口径）。
 */
export function pruneStructuredDraftColumns(draft, optionMetaById = {}) {
  const rows = Array.isArray(draft?.rows) ? draft.rows : [];
  const selected = (draft?.selectedOptionIds || []).map(String).filter(Boolean);
  const hasMeta = optionMetaById && typeof optionMetaById === 'object'
    && Object.keys(optionMetaById).length > 0;
  const kept = selected.filter((id) => {
    const role = String(optionMetaById?.[id]?.role || '').toUpperCase();
    const isKey = role === 'KEY';
    if (hasMeta) {
      if (isKey) return true;
      if (!rows.length) return false;
      return rows.every((row) => String(row?.values?.[id] ?? '').trim());
    }
    // 无角色元数据时：有值的列才保留（避免空选填列进 payload）
    if (!rows.length) return true;
    const anyFilled = rows.some((row) => String(row?.values?.[id] ?? '').trim());
    const allFilled = rows.every((row) => String(row?.values?.[id] ?? '').trim());
    return anyFilled ? allFilled : false;
  });
  return {
    ...draft,
    selectedOptionIds: kept.length ? kept : selected.filter((id) => (
      String(optionMetaById?.[id]?.role || '').toUpperCase() === 'KEY'
    )),
  };
}

export function buildSizeChartDataFromDraft(draft, optionMetaById = {}) {
  const pruned = pruneStructuredDraftColumns(draft, optionMetaById);
  const headers = [SIZE_HEADER];
  (pruned.selectedOptionIds || []).forEach((id) => {
    const meta = optionMetaById[id] || { id, name: id };
    headers.push({ id: meta.id, name: meta.name || meta.id });
  });
  const rows = (pruned.rows || [])
    .map((row) => {
      const size = String(row.size || '').trim();
      if (!size && !pruned.isOneSize) return null;
      const cells = [{ value: size || (pruned.isOneSize ? 'ONE_SIZE' : '') }];
      (pruned.selectedOptionIds || []).forEach((id) => {
        cells.push({ value: String(row.values?.[id] ?? '').trim() });
      });
      return { cells };
    })
    .filter(Boolean);
  return {
    table: { headers, rows },
    size_types: (pruned.sizeTypes || []).filter(Boolean),
    description: String(pruned.description || '').trim() || undefined,
    is_one_size: Boolean(pruned.isOneSize),
    measurement_type: pruned.measurementType || 'BODY_MEASUREMENTS',
    unit_system: pruned.unitSystem || 'METRIC',
  };
}

/**
 * @param {{ requireMeta?: boolean, optionMetaById?: object }} [options]
 *   requireMeta=false：跳过测量类型/单位制（留给保存弹窗再填）
 *   optionMetaById：含 role=KEY / RECOMMEND；KEY 必填，选填列可整列留空（不可半填）
 */
export function validateStructuredSizeChartDraft(
  draft,
  { required = true, requireMeta = true, optionMetaById = {} } = {},
) {
  if (!required) return { ok: true };
  if (!draft?.selectedOptionIds?.length) {
    return { ok: false, message: chartText('sizeChartPanel.needOneMeasure', fixedUi('请至少选择一列测量项')) };
  }
  const rows = draft.rows || [];
  if (!rows.length) {
    return { ok: false, message: draft?.isOneSize ? chartText('sizeChartPanel.fillOneSize', fixedUi('请完善均码测量值')) : chartText('sizeChartPanel.pickSize', fixedUi('请先选择尺寸')) };
  }
  const hasMeta = optionMetaById && typeof optionMetaById === 'object'
    && Object.keys(optionMetaById).length > 0;
  const selectedIds = (draft.selectedOptionIds || []).map(String);
  const keyIds = hasMeta
    ? selectedIds.filter((id) => String(optionMetaById[id]?.role || '').toUpperCase() === 'KEY')
    : selectedIds;
  const optionalIds = hasMeta
    ? selectedIds.filter((id) => String(optionMetaById[id]?.role || '').toUpperCase() !== 'KEY')
    : [];

  for (const row of rows) {
    if (!draft.isOneSize && !String(row.size || '').trim()) {
      return { ok: false, message: chartText('sizeChartPanel.needSizeName', fixedUi('请填写尺码名称（Size）')) };
    }
    for (const id of keyIds) {
      if (!String(row.values?.[id] ?? '').trim()) {
        const label = optionMetaById[id]?.name || id;
        return { ok: false, message: chartText('sizeChartPanel.needKeyMeasure', fixedUiTpl('请填写必填测量项「{0}」', label), { label }) };
      }
    }
  }
  // 选填列：要么整列空，要么整列满（不允许半填）
  for (const id of optionalIds) {
    const filled = rows.map((row) => String(row.values?.[id] ?? '').trim());
    const any = filled.some(Boolean);
    const all = filled.every(Boolean);
    if (any && !all) {
      const label = optionMetaById[id]?.name || id;
      return { ok: false, message: chartText('sizeChartPanel.optionalCol', fixedUiTpl('选填列「{0}」请整列填完或整列留空', label), { label }) };
    }
  }
  if (!hasMeta) {
    // 无角色信息时保持旧口径：selected 列都要有值
    for (const row of rows) {
      for (const id of selectedIds) {
        if (!String(row.values?.[id] ?? '').trim()) {
          return { ok: false, message: chartText('sizeChartPanel.fillAll', fixedUi('测量值请填完整，不可留空')) };
        }
      }
    }
  } else if (!keyIds.length) {
    // 有元数据但没有任何 KEY：至少要有一列完整测量值
    const anyCompleteCol = selectedIds.some((id) => (
      rows.every((row) => String(row.values?.[id] ?? '').trim())
    ));
    if (!anyCompleteCol) {
      return { ok: false, message: chartText('sizeChartPanel.needOneCol', fixedUi('请至少完整填写一列测量值')) };
    }
  }
  if (requireMeta) {
    if (!draft.measurementType) {
      return { ok: false, message: chartText('sizeChartPanel.pickMeasureType', fixedUi('请选择测量类型')) };
    }
    if (!draft.unitSystem) {
      return { ok: false, message: chartText('sizeChartPanel.pickUnit', fixedUi('请选择单位制')) };
    }
  }
  return { ok: true };
}

/**
 * TikTok 结构化尺码表：选类目 → 选尺寸 → 出表格（交互对齐 SHEIN 尺码库）。
 * 保存仍走 Create/Edit Size Chart 202608。
 */
export default function TiktokStructuredSizeChartPanel({
  shopId = '',
  categoryId = '',
  initialDraft = null,
  onDraftChange,
  allowWithoutCategory = false,
  onRulesMetaChange,
  disabled = false,
  /**
   * 是否在主表单展示「测量类型 / 单位制」。
   * 默认 false：保存弹窗再选（建尺码表 / 产品模板与「建尺码表」解耦）。
   */
  showMeasurementMeta = false,
}) {
  useTranslation('workbench');
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(() => hydrateStructuredDraft({}));
  const [saleSizeOptions, setSaleSizeOptions] = useState([]);
  const [measureLabelVersion, setMeasureLabelVersion] = useState(0);

  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const onRulesMetaChangeRef = useRef(onRulesMetaChange);
  onRulesMetaChangeRef.current = onRulesMetaChange;

  useEffect(() => {
    if (!initialDraft || typeof initialDraft !== 'object') return;
    if (Array.isArray(initialDraft.rows) || initialDraft.measurementType) {
      setDraft({ ...hydrateStructuredDraft({}), ...initialDraft });
      return;
    }
    setDraft(hydrateStructuredDraft({ sizeChartData: initialDraft }));
  }, [initialDraft]);

  useEffect(() => {
    onDraftChangeRef.current?.(draft);
  }, [draft]);

  useEffect(() => {
    if (!shopId || !categoryId) {
      setGroups([]);
      setSaleSizeOptions([]);
      setSupported(true);
      setError('');
      onRulesMetaChangeRef.current?.({ supported: true, required: false, groups: [] });
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      tiktokPlatformAPI.getPublishCategoryRules(shopId, categoryId),
      tiktokPlatformAPI.getAttributeTemplate(shopId, { catId: Number(categoryId) }).catch(() => null),
    ])
      .then(([rulesRes, attrsRes]) => {
        if (cancelled) return;
        const sizeChart = unwrapSizeChartRules(rulesRes);
        const isSupported = sizeChart?.is_supported !== false;
        const isRequired = sizeChart?.is_required === true;
        setSupported(isSupported);
        const list = Array.isArray(sizeChart?.measurement_and_options)
          ? sizeChart.measurement_and_options
          : [];
        setGroups(list);
        onRulesMetaChangeRef.current?.({
          supported: isSupported,
          required: isRequired,
          groups: list,
        });

        // 销售属性中的尺码选项（有则优先用于「尺寸」多选）
        const attrs = Array.isArray(attrsRes?.data?.[0]?.attribute_infos)
          ? attrsRes.data[0].attribute_infos
          : (Array.isArray(attrsRes?.attribute_infos) ? attrsRes.attribute_infos : []);
        const saleAttrs = attrs.filter((a) => Number(a.attribute_type ?? a.attributeType) === 1);
        const sizeLike = saleAttrs.find((a) => /size|尺码|尺寸|规格/i.test(String(a.attribute_name || a.attribute_name_cn || '')))
          || saleAttrs[0]
          || null;
        const saleValues = (sizeLike?.attribute_value_info_list || []).map((v) => ({
          id: String(v.attribute_value_id ?? v.id ?? ''),
          name: String(v.attribute_value_name || v.attribute_value || v.name || '').trim(),
        })).filter((v) => v.id && v.name);
        setSaleSizeOptions(saleValues);

        setDraft((prev) => {
          const measurementType = prev.measurementType && list.some(
            (g) => (g.measurement_type || g.measurementType) === prev.measurementType,
          )
            ? prev.measurementType
            : pickInitialMeasurementType(list);
          const measureOpts = optionsForType(list, measurementType);
          const optionIds = new Set(measureOpts.map((o) => o.id));
          // 始终带上当前测量类型下的全部列（新建/换类目不依赖用户勾选）
          let selected = (prev.selectedOptionIds || []).filter((id) => optionIds.has(id));
          if (!selected.length || selected.length < measureOpts.length) {
            selected = defaultSelectedOptionIdsForType(list, measurementType);
          }
          const sizeOptions = saleValues;
          let sizeKeys = (prev.selectedSizeKeys || []).filter(Boolean);
          if (!sizeKeys.length && prev.rows?.length) {
            sizeKeys = prev.rows.map((r) => r.sizeKey || (r.size ? makeCustomSpecKey(r.size) : '')).filter(Boolean);
          }
          const rows = prev.isOneSize
            ? [{ sizeKey: 'ONE_SIZE', size: 'ONE_SIZE', values: prev.rows?.[0]?.values || {} }]
            : rowsFromSizeKeys(sizeKeys, sizeOptions, prev.rows);
          return {
            ...prev,
            measurementType,
            selectedOptionIds: selected,
            selectedSizeKeys: sizeKeys,
            rows,
          };
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setGroups([]);
        setSaleSizeOptions([]);
        setError(e?.message || chartText('sizeChartPanel.rulesLoadFail', fixedUi('加载类目尺码规则失败')));
        showToast(e?.message || chartText('sizeChartPanel.rulesLoadFail', fixedUi('加载类目尺码规则失败')), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, categoryId]);

  const measureOptions = useMemo(
    () => optionsForType(groups, draft.measurementType),
    [groups, draft.measurementType],
  );
  const measurementTypeChoices = useMemo(() => {
    const seen = new Set();
    const list = (groups || []).map((g) => {
      const id = g.measurement_type || g.measurementType;
      if (!id || seen.has(id)) return null;
      seen.add(id);
      return { id, label: fixedUi(MEASUREMENT_TYPE_LABELS[id] || id) };
    }).filter(Boolean);
    if (!list.length) {
      return Object.entries(MEASUREMENT_TYPE_LABELS).map(([id, label]) => ({ id, label: fixedUi(label) }));
    }
    return list;
  }, [groups]);

  // 尺寸只取平台下发（类目销售属性的 values）；平台未下发时仅保留「均码」+ 自定义输入，
  // 不注入固定成人尺码预设（童装/鞋类会用错码）。
  const sizeOptions = useMemo(() => {
    const rest = saleSizeOptions
      .filter((o) => !isOneSizeKey(o.id) && !/均码|one\s*size/i.test(String(o.name || '')));
    return [ONE_SIZE_OPTION, ...rest];
  }, [saleSizeOptions]);

  const optionMetaById = useMemo(() => {
    const map = {};
    measureOptions.forEach((o) => { map[o.id] = o; });
    return map;
  }, [measureOptions]);

  useEffect(() => {
    const selected = (draft.selectedOptionIds || [])
      .map((id) => optionMetaById[id] || { id, name: id })
      .filter((o) => o.id);
    if (!selected.length) return undefined;
    let cancelled = false;
    void resolveTiktokSizeChartMeasureLabels(selected)
      .then(() => {
        if (!cancelled) setMeasureLabelVersion((v) => v + 1);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [draft.selectedOptionIds, optionMetaById]);

  const selectedSizeKeys = draft.selectedSizeKeys || [];
  const showSizePicker = Boolean(categoryId) && supported;
  const showTable = Boolean(categoryId)
    && supported
    && selectedSizeKeys.length > 0
    && (measureOptions.length > 0 || (draft.selectedOptionIds || []).length > 0);

  const patchDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const onSizeKeysChange = (keys) => {
    const prevKeys = selectedSizeKeys;
    const prevSet = new Set(prevKeys);
    let next = [...new Set((keys || []).map(String).filter(Boolean))];
    const added = next.filter((k) => !prevSet.has(k));
    // 新选均码 → 只保留均码；在已有均码时再选其它 → 去掉均码
    if (added.some(isOneSizeKey)) {
      next = [ONE_SIZE_KEY];
    } else if (next.some(isOneSizeKey) && next.length > 1) {
      next = next.filter((k) => !isOneSizeKey(k));
    }
    const isOneSize = next.length === 1 && isOneSizeKey(next[0]);
    setDraft((prev) => ({
      ...prev,
      isOneSize,
      selectedSizeKeys: next,
      rows: isOneSize
        ? [{
          sizeKey: ONE_SIZE_KEY,
          size: 'ONE_SIZE',
          values: (prev.rows || []).find((r) => isOneSizeKey(r.sizeKey))?.values
            || prev.rows?.[0]?.values
            || {},
        }]
        : rowsFromSizeKeys(next, sizeOptions, prev.rows),
    }));
  };

  const updateCell = (index, optionId, value) => {
    setDraft((prev) => {
      const rows = [...(prev.rows || [])];
      const row = { ...(rows[index] || { size: '', values: {} }) };
      row.values = { ...(row.values || {}), [optionId]: value };
      rows[index] = row;
      return { ...prev, rows };
    });
  };

  /** 表格列：始终用平台下发的全部测量项（不提供勾选 UI） */
  const tableMeasureIds = useMemo(() => {
    if (measureOptions.length) return measureOptions.map((o) => o.id);
    return (draft.selectedOptionIds || []).map(String).filter(Boolean);
  }, [measureOptions, draft.selectedOptionIds]);

  // 与表格展示保持一致，避免保存时漏掉可选列
  useEffect(() => {
    if (!measureOptions.length) return;
    const allIds = measureOptions.map((o) => o.id);
    setDraft((prev) => {
      const cur = (prev.selectedOptionIds || []).map(String);
      if (cur.length === allIds.length && cur.every((id, i) => id === allIds[i])) return prev;
      return { ...prev, selectedOptionIds: allIds };
    });
  }, [measureOptions]);

  if (!categoryId && !allowWithoutCategory) {
    return null;
  }

  return (
    <div className="tko-size-chart-struct">
      {!categoryId && allowWithoutCategory ? (
        <p className="tko-wizard__inline-hint">{chartText('sizeChartPanel.unbound', fixedUi("未绑定类目：按已有结构化内容编辑；保存后建议同步以回填类目。"))}</p>
      ) : null}
      {loading ? <p className="tko-wizard__inline-hint">{chartText('sizeChartPanel.loadingCatRules', fixedUi("正在加载类目尺码规则…"))}</p> : null}
      {error ? <p className="tko-field__error">{error}</p> : null}
      {!loading && categoryId && !supported ? (
        <p className="tko-wizard__inline-hint">{chartText('sizeChartPanel.unsupported', fixedUi("该类目不支持尺码表（平台 is_supported=false）。"))}</p>
      ) : null}

      {!loading && categoryId && supported ? (
        <>
          {showMeasurementMeta ? (
            <div
              className="tko-form-grid"
              style={{
                marginBottom: 12,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px 16px',
              }}
            >
              <label className="tko-field" style={{ margin: 0 }}>
                <span className="tko-field__label">{chartText('sizeChartPanel.measureType', fixedUi("测量类型"))} <RequiredMark /></span>
                <select
                  className="tko-input"
                  value={draft.measurementType}
                  disabled={disabled}
                  onChange={(e) => {
                    const measurementType = e.target.value;
                    const selected = defaultSelectedOptionIdsForType(groups, measurementType);
                    patchDraft({ measurementType, selectedOptionIds: selected });
                  }}
                >
                  {measurementTypeChoices.map((opt) => (
                    <option key={opt.id} value={opt.id}>{chartText(opt.id === 'GENERIC_MEASUREMENTS' ? 'sizeChartPanel.mtGeneric' : opt.id === 'BODY_MEASUREMENTS' ? 'sizeChartPanel.mtBody' : opt.id === 'PRODUCT_MEASUREMENTS' ? 'sizeChartPanel.mtProduct' : 'sizeChartPanel.measureType', opt.label)}</option>
                  ))}
                </select>
              </label>
              <label className="tko-field" style={{ margin: 0 }}>
                <span className="tko-field__label">{chartText('sizeChartPanel.unitSystem', fixedUi("单位制"))} <RequiredMark /></span>
                <select
                  className="tko-input"
                  value={draft.unitSystem}
                  disabled={disabled}
                  onChange={(e) => patchDraft({ unitSystem: e.target.value })}
                >
                  {UNIT_SYSTEM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{chartText(opt.value === 'METRIC' ? 'sizeChartPanel.unitMetric' : 'sizeChartPanel.unitImperial', opt.label)}</option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          {showSizePicker ? (
            <div className="tko-template-create-sizes">
              <span className="tko-field__label">{chartText('sizeChartPanel.sizeWord', fixedUi("尺寸"))} <RequiredMark /></span>
              <SpecValuePicker
                options={sizeOptions}
                value={selectedSizeKeys}
                onChange={onSizeKeysChange}
                placeholder={chartText('sizeChartPanel.sizePh', fixedUi('请选择或自定义尺寸，回车添加（均码与其它尺码互斥）'))}
                disabled={disabled || loading}
                emptyText={chartText('sizeChartPanel.noSizeOpt', fixedUi('暂无尺寸选项'))}
                disabledHint={loading ? chartText('tiktokPublish.loading', fixedUi("加载中…")) : chartText('sizeChartPanel.notSelectable', fixedUi('暂不可选'))}
                allowCustom={!selectedSizeKeys.some(isOneSizeKey)}
                addMode
              />
              {selectedSizeKeys.some(isOneSizeKey) ? (
                <p className="tko-wizard__inline-hint">{chartText('sizeChartPanel.oneSizeLocked', fixedUi("已选均码，不可再选其它尺码；清除后可多选。"))}</p>
              ) : null}
            </div>
          ) : null}

          {showTable ? (
            <div
              className="tko-publish__table-wrap tko-publish__table-wrap--scroll tko-size-chart-table-scroll"
              style={{ marginTop: 12 }}
            >
              <table className="tko-publish__table tko-size-chart-table">
                <thead>
                  <tr>
                    <th className="tko-size-chart-table__size">{chartText('sizeChartPanel.sizeWord', fixedUi("尺寸"))}</th>
                    {tableMeasureIds.map((id) => (
                      <th key={`${id}-${measureLabelVersion}`}>
                        {getCachedTiktokSizeChartMeasureLabel(optionMetaById[id] || { id })
                          || formatTiktokSizeChartMeasureLabel(optionMetaById[id] || { id })}
                        {optionMetaById[id]?.role === 'KEY' ? <RequiredMark /> : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(draft.rows || []).map((row, idx) => (
                    <tr key={row.sizeKey || `row-${idx}`}>
                      <td className="tko-size-chart-table__size">
                        {isOneSizeKey(row.sizeKey) || draft.isOneSize
                          ? fixedUi('均码')
                          : (row.size || '—')}
                      </td>
                      {tableMeasureIds.map((id) => (
                        <td key={id}>
                          <input
                            className="tko-input"
                            value={row.values?.[id] || ''}
                            disabled={disabled}
                            placeholder="—"
                            onChange={(e) => updateCell(idx, id, e.target.value)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {!loading && showSizePicker && !selectedSizeKeys.length ? (
            <p className="tko-wizard__inline-hint">{fixedUi("请先选择尺寸，表格将按所选尺码自动生成。")}</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
